import { useMemo } from 'react';
import union from 'lodash/union';
import {
	type Operand,
	CLAUSE_TYPE_TERMINAL,
	OPERAND_TYPE_KEYWORD,
	OPERAND_TYPE_LIST,
	OPERAND_TYPE_VALUE,
	type TerminalClause,
} from '@atlaskit/jql-ast';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { UnsupportedRefinementError } from '../unsupported-refinement-error/index.tsx';
import { useAST, useClauseMap, useJQL } from '../index.tsx';
import { JqlValuesCollectingVisitor } from '../jql-values-collecting-visitor/index.tsx';

const getOperands = (clauseName: string, operand: Operand, jql: string): string[] => {
	switch (operand?.operandType) {
		case OPERAND_TYPE_VALUE: {
			return fg('filter_refinement_in_tmp_backlog_backend') ? [operand.text] : [operand.value];
		}
		case OPERAND_TYPE_KEYWORD:
			return [operand.value];
		case OPERAND_TYPE_LIST: {
			return union(
				...operand.values.map((operandValue) => getOperands(clauseName, operandValue, jql)),
			);
		}
		default: {
			throw new UnsupportedRefinementError(
				jql,
				`Unsupported operand type (${operand?.operandType}) for ${clauseName}`,
			);
		}
	}
};

export const getSelectedValuesForField = (
	clauses: TerminalClause[] | undefined,
	clauseName: string,
	jql: string,
) => {
	if (!clauses || clauses.length === 0) {
		return [];
	}

	// TODO: add per-picker clause validation logic
	if (!fg('filter_refinement_m2_date_fields') && clauses.length > 1) {
		throw new UnsupportedRefinementError(jql, `Too many clauses for ${clauseName}`);
	}

	const [clause] = clauses;

	if (clause.clauseType !== CLAUSE_TYPE_TERMINAL) {
		throw new UnsupportedRefinementError(
			jql,
			`Unsupported clause type (${clause.clauseType}) for ${clauseName}`,
		);
	}

	const { operand } = clause;

	if (!operand) {
		throw new UnsupportedRefinementError(jql, `No operand for ${clauseName}`);
	}

	return getOperands(clauseName, operand, jql);
};

/**
 * @deprecated because useClauseMap is also deprecated. Check useSelectedValues in select picker.
 */
export const useSelectedValues = (clauseName: string): string[] => {
	const clauseMap = useClauseMap();

	const jql = useJQL();

	const clauses = clauseMap[clauseName.toLowerCase()];

	return useMemo(() => {
		return getSelectedValuesForField(clauses, clauseName, jql);
	}, [clauses, clauseName, jql]);
};

export const useNumberOfSelectedValuesOld = () => {
	const clauseMap = useClauseMap();

	const jql = useJQL();

	return useMemo(() => {
		let numberOfSelectedValues = 0;
		const clauseName = Object.keys(clauseMap);

		clauseName.forEach((name) => {
			const clauses = clauseMap[name];
			const fieldValues = getSelectedValuesForField(clauses, name, jql);
			numberOfSelectedValues += fieldValues.length;
		});

		return numberOfSelectedValues;
	}, [clauseMap, jql]);
};

const useNumberOfSelectedValuesNew = (projectContext?: string) => {
	const ast = useAST();

	const valuesVisitor = useMemo(
		() => new JqlValuesCollectingVisitor(projectContext),
		[projectContext],
	);

	return useMemo(() => {
		if (ast.query) {
			const result = valuesVisitor.visit(ast.query);
			if (result.isTooComplex) {
				return 1;
			}
			return result.count;
		}
		return 0;
	}, [ast.query, valuesVisitor]);
};

export const useNumberOfSelectedValues = functionWithCondition(
	() => fg('list_lovability_improving_filters'),
	useNumberOfSelectedValuesNew,
	useNumberOfSelectedValuesOld,
);
